import type { CurrencyCode } from "./type";

export type SoftwareMock = {
  name: string;
  softwareId: string;
  vendorName: string;
  vendorAvatarUrl: string;
  nextRenewalDate: string;
  status: "Active" | "Inactive";
  assigned: number;
  total: number;
  hasPricing: boolean;
  currencyCode: CurrencyCode;
  detail: {
    softwareId: string;
    softwareName: string;
    softwareAlias: string;
    softwareType: string;
    category: string;
    description: string;
  };
  link: {
    servicePageUrl: string;
    termsOfUseUrl: string;
    privacyPolicyUrl: string;
    pricingPageUrl: string;
  };
  responsibility: {
    responsiblePersonId: string;
    responsiblePersonName: string;
    responsibleDepartment: string;
  };
  vendor: {
    vendorId: string;
    vendorName: string;
    vendorContact: string;
    vendorUrl: string;
  };
  maintenanceSupport: {
    supportLevel: string;
    updateFrequency: string;
  };
  provisioningDistribution: {
    deploymentMethod: string;
    platformUsage: string;
    linkedDeviceId: string;
    linkedSoftwareId: string;
    mdmManaged: string;
  };
  compatibilityEnvironment: {
    requiredOs: string;
    compatibleBrowsers: string;
    supportedLanguages: string;
  };
  securityCompliance: {
    securityCertifications: string;
    dataLocation: string;
    gdprCompliance: string;
    accessControlType: string;
  };
  operationalStatus: {
    usageStatus: string;
    retirementDate: string;
  };
  architecture: {
    versionBuildInformation: {
      version: string;
      buildNumber: string;
      releaseDate: string;
      patchStatus: string;
      patchVersion: string;
      endOfSupportDate: string;
      versionPolicy: string;
    };
    softwareComponent: {
      componentId: string;
      componentName: string;
      requesterSubmitter: string;
      runtime: string;
    };
    softwareDependency: {
      dependencyId: string;
      dependsOnType: string;
      dependsOnId: string;
      minVersion: string;
      maxVersion: string;
      requiredFlag: string;
    };
    softwareInstance: {
      instanceId: string;
      instanceType: string;
      tenantDomain: string;
      environment: string;
      region: string;
      linkedSystems: string;
    };
    installInformation: {
      installationDate: string;
      installationPath: string;
      installationMethod: string;
      installationMediaInfo: string;
      installedDevicesCount: string;
    };
    executionRequirements: {
      requiredMemory: string;
      requiredDiskSpace: string;
      dependentSoftware: string;
      executionMode: string;
    };
    securityCompliance: {
      requiresAdminPrivilege: string;
      autoUpdateSetting: string;
      updateServerUrl: string;
      tamperDetectionEnabled: string;
    };
    uninstallInformation: {
      uninstallationDate: string;
      uninstallationMethod: string;
      uninstalledBy: string;
      uninstallationReason: string;
    };
  };
  contractsCost: {
    contractInformation: {
      contractId: string;
      contractName: string;
      contractType: string;
      contractStatus: string;
      contractStartDate: string;
      contractEndDate: string;
      autoRenew: string;
      renewalWindow: string;
      cancellationDeadline: string;
      noticePeriod: string;
      contractAmount: string;
      billingCycle: string;
      paymentMethod: string;
      paymentTerms: string;
      paymentCurrency: string;
      supplierId: string;
      supplierContact: string;
      contractFileUrl: string;
      contractNotes: string;
    };
    planInformation: {
      planId: string;
      planName: string;
      planCode: string;
      planType: string;
      pricingModel: string;
      pricingDriver: string;
      currency: string;
      unitPrice: string;
      baseFee: string;
      variableFee: string;
      usageUnit: string;
      usageLimit: string;
      includedFeatures: string;
      planNotes: string;
    };
    budgetCost: {
      costMonth: string;
      budgetedCost: string;
      actualCost: string;
      varianceReason: string;
      costCenter: string;
      invoiceNumber: string;
      taxRate: string;
    };
  };
  activityMonitoring: {
    accountAccessActivity: Array<{
      accountId: string;
      accountName: string;
      accountAvatar: string;
      activityType: string;
      activityDescription: string;
      deviceId: string;
      deviceName: string;
      deviceType: string;
      deviceModel: string;
      ipAddress: string;
    }>;
    accountAccessAudit: {
      auditReviewStatus: string;
      reviewedBy: string;
      reviewedAt: string;
    };
  };
};

export type SoftwareOverviewMonthlyCostMock = {
  cost: number;
  currencyCode: CurrencyCode;
  monthOverMonthRate: number;
};

export type SoftwareOverviewUserMock = {
  appUserCount: number;
  peopleCount: number;
  monthOverMonthChange: number;
};

export type SoftwareOverviewMonthlyActiveUserRateMock = {
  activeUserRate: number;
  monthOverMonthRate: number;
};

export type SoftwareOverviewRenewalDateMock = {
  renewalDate: Date | null;
};

export type SoftwareOverviewSavingCostMock = {
  savingCost: number;
  currencyCode: CurrencyCode;
  monthOverMonthChange: number;
};

export type SoftwareOverviewBillingCostMock = {
  monthlyCosts: Array<{
    year: number;
    month: number;
    cost: number;
    cumulativeCost: number;
  }>;
  forecastedCosts: Array<{
    year: number;
    month: number;
    forecastedCost: number;
    forecastedCumulativeCost: number;
  }>;
  monthlyUsers: Array<{
    year: number;
    month: number;
    userCount: number;
  }>;
};

export type SoftwareOverviewUserGrowthMock = {
  monthlyUsers: Array<{
    year: number;
    month: number;
    userCount: number;
    cumulativeUserCount: number;
  }>;
};

export type SoftwareOverviewRecommendedActionsMock = Array<{
  actionType: "SET_LICENSE_SEAT" | "RE_AUTHENTICATION";
  title: string;
  description: string;
  actionLabel: string;
}>;

export type RefreshResourcesStatusMock = {
  status: "IN_PROGRESS" | "COMPLETED" | "FAILED";
};

const PowerPointMock: SoftwareMock = {
  name: "Microsoft PowerPoint",
  softwareId: "so-098492",
  vendorName: "Microsoft Corporation",
  vendorAvatarUrl: "/software_icons/microsoft.png",
  nextRenewalDate: "2025-09-01",
  status: "Active",
  assigned: 120,
  total: 150,
  hasPricing: true,
  currencyCode: "USD",
  detail: {
    softwareId: "so-098492",
    softwareName: "Microsoft PowerPoint",
    softwareAlias: "PPT",
    softwareType: "Productivity",
    category: "Office Suite",
    description:
      "Presentation authoring tool with collaboration and cloud integration",
  },
  link: {
    servicePageUrl: "https://www.microsoft.com/powerpoint",
    termsOfUseUrl: "https://www.microsoft.com/microsoft-365/legal/terms-of-use",
    privacyPolicyUrl: "https://privacy.microsoft.com/privacystatement",
    pricingPageUrl: "https://www.microsoft.com/microsoft-365/business",
  },
  responsibility: {
    responsiblePersonId: "EMP-102",
    responsiblePersonName: "Jordan Hayes",
    responsibleDepartment: "IT Department",
  },
  vendor: {
    vendorId: "VND-001",
    vendorName: "Microsoft Corporation",
    vendorContact: "support@microsoft.com",
    vendorUrl: "https://www.microsoft.com",
  },
  maintenanceSupport: {
    supportLevel: "Standard Enterprise Support",
    updateFrequency: "Monthly",
  },
  provisioningDistribution: {
    deploymentMethod: "Cloud-based",
    platformUsage: "Desktop/Mobile",
    linkedDeviceId: "-",
    linkedSoftwareId: "-",
    mdmManaged: "Yes",
  },
  compatibilityEnvironment: {
    requiredOs: "Windows/macOS",
    compatibleBrowsers: "Chrome/Edge/Safari",
    supportedLanguages: "English/Japanse/Chinese",
  },
  securityCompliance: {
    securityCertifications: "ISO 27001",
    dataLocation: "US",
    gdprCompliance: "Completed",
    accessControlType: "SSO",
  },
  operationalStatus: {
    usageStatus: "Active",
    retirementDate: "-",
  },
  architecture: {
    versionBuildInformation: {
      version: "16.0.17628",
      buildNumber: "17628.20202",
      releaseDate: "2025-06-15",
      patchStatus: "Latest",
      patchVersion: "KB5023431",
      endOfSupportDate: "-",
      versionPolicy: "-",
    },
    softwareComponent: {
      componentId: "CMP-001",
      componentName: "PowerPoint Core Engine",
      requesterSubmitter: "-",
      runtime: "-",
    },
    softwareDependency: {
      dependencyId: "-",
      dependsOnType: "-",
      dependsOnId: "-",
      minVersion: "-",
      maxVersion: "-",
      requiredFlag: "-",
    },
    softwareInstance: {
      instanceId: "INST-0001",
      instanceType: "Multi-tenant SaaS Instance",
      tenantDomain: "dresscode.onmicrosoft.com",
      environment: "Production",
      region: "Japan",
      linkedSystems: "-",
    },
    installInformation: {
      installationDate: "2025-03-01",
      installationPath: "C:\\Program Files\\Microsoft Office\\PowerPoint",
      installationMethod: "Click-to-Run (M365)",
      installationMediaInfo: "Online Installer",
      installedDevicesCount: "120",
    },
    executionRequirements: {
      requiredMemory: "4 GB",
      requiredDiskSpace: "2 GB",
      dependentSoftware: "Office Core; OneDrive Sync",
      executionMode: "Desktop App; Web App",
    },
    securityCompliance: {
      requiresAdminPrivilege: "No",
      autoUpdateSetting: "Enabled",
      updateServerUrl: "https://update.microsoft.com",
      tamperDetectionEnabled: "Yes",
    },
    uninstallInformation: {
      uninstallationDate: "-",
      uninstallationMethod: "Manual Removal",
      uninstalledBy: "-",
      uninstallationReason: "-",
    },
  },
  contractsCost: {
    contractInformation: {
      contractId: "CTR-2025-001",
      contractName: "Microsoft 365 Business Standard",
      contractType: "Subscription Agreement",
      contractStatus: "Active",
      contractStartDate: "2025-03-01",
      contractEndDate: "2026-03-01",
      autoRenew: "Yes",
      renewalWindow: "30 days before contract end date",
      cancellationDeadline: "30 days prior to renewal date",
      noticePeriod: "30 days",
      contractAmount: "$1,200,000 (tax excluded)",
      billingCycle: "Annual",
      paymentMethod: "Bank transfer",
      paymentTerms: "Net 30 days",
      paymentCurrency: "USD",
      supplierId: "VND-001",
      supplierContact: "support@microsoft.com",
      contractFileUrl: "https://drive.example.com/contracts/CTR-2025-001",
      contractNotes: "Standard enterprise agreement",
    },
    planInformation: {
      planId: "PLAN-M365-001",
      planName: "Microsoft 365 Business Standard",
      planCode: "M365STD",
      planType: "Per-user subscription",
      pricingModel: "Per user per month",
      pricingDriver: "Number of active users",
      currency: "USD",
      unitPrice: "$12.50",
      baseFee: "$0",
      variableFee: "Per user billing",
      usageUnit: "Active user account",
      usageLimit: "Unlimited",
      includedFeatures: "Office apps, 1TB OneDrive, Teams, Exchange",
      planNotes: "Standard business plan",
    },
    budgetCost: {
      costMonth: "2025-03-01",
      budgetedCost: "$1,500",
      actualCost: "$1,500",
      varianceReason: "On budget",
      costCenter: "IT Department",
      invoiceNumber: "INV-2025-1001",
      taxRate: "10%",
    },
  },
  activityMonitoring: {
    accountAccessActivity: [
      {
        accountId: "kristin.watson",
        accountName: "Kristin Watson",
        accountAvatar: "https://example.com/avatars/kristin.jpg",
        activityType: "Logout",
        activityDescription: "-",
        deviceId: "KKW052JFYM",
        deviceName: "Macbook Air",
        deviceType: "laptop",
        deviceModel: "KKW052JFYM",
        ipAddress: "45.72.198.14",
      },
      {
        accountId: "jordan.hayes",
        accountName: "Jordan Hayes",
        accountAvatar: "https://example.com/avatars/jordan.jpg",
        activityType: "View Files",
        activityDescription: "Shared file via link",
        deviceId: "CC001FHWO",
        deviceName: "Macbook Pro",
        deviceType: "laptop",
        deviceModel: "CC001FHWO",
        ipAddress: "103.211.54.87",
      },
      {
        accountId: "taylor.brooks",
        accountName: "Taylor Brooks",
        accountAvatar: "https://example.com/avatars/taylor.jpg",
        activityType: "Change Settings",
        activityDescription: "Attempted password change",
        deviceId: "KKW052JFYM",
        deviceName: "Macbook Air",
        deviceType: "laptop",
        deviceModel: "KKW052JFYM",
        ipAddress: "185.64.129.203",
      },
      {
        accountId: "quinn.carter",
        accountName: "Quinn Carter",
        accountAvatar: "https://example.com/avatars/quinn.jpg",
        activityType: "Login",
        activityDescription: "-",
        deviceId: "CC054FHHD",
        deviceName: "Macbook Pro",
        deviceType: "laptop",
        deviceModel: "CC054FHHD",
        ipAddress: "217.92.14.66",
      },
    ],
    accountAccessAudit: {
      auditReviewStatus: "Confirmed",
      reviewedBy: "Jordan Hayes",
      reviewedAt: "2025-03-01",
    },
  },
};

const WindowsServerMock: SoftwareMock = {
  name: "Windows Server 2022 Standard",
  softwareId: "so-ws2022",
  vendorName: "Microsoft",
  vendorAvatarUrl: "/software_icons/microsoft.png",
  nextRenewalDate: "2025-04-01",
  status: "Active",
  assigned: 10,
  total: 10,
  hasPricing: true,
  currencyCode: "USD",
  detail: {
    softwareId: "20250801-00123",
    softwareName: "Windows Server 2022",
    softwareAlias: "Windows Server 2022",
    softwareType: "OS",
    category: "-",
    description:
      "Used as the server operating system for internal business systems, including authentication, file sharing, and application hosting.",
  },
  link: {
    servicePageUrl:
      "https://www.microsoft.com/ja-jp/d/windows-server-2022-standard-cal/",
    termsOfUseUrl: "-",
    privacyPolicyUrl: "-",
    pricingPageUrl: "-",
  },
  responsibility: {
    responsiblePersonId: "R-111",
    responsiblePersonName: "Carter Kenter",
    responsibleDepartment: "IT Department",
  },
  vendor: {
    vendorId: "V-1",
    vendorName: "Microsoft",
    vendorContact: "support@microsoft.com",
    vendorUrl: "www.microsoft.com",
  },
  maintenanceSupport: {
    supportLevel: "mail",
    updateFrequency: "Irregular",
  },
  provisioningDistribution: {
    deploymentMethod: "Cloud",
    platformUsage: "Desktop / mobile / Web",
    linkedDeviceId: "HP Z2 Tower G9",
    linkedSoftwareId: "-",
    mdmManaged: "Yes",
  },
  compatibilityEnvironment: {
    requiredOs: "Windows / macOS / Linux / Android / iOS",
    compatibleBrowsers: "Chrome / Edge / Safari / Firefox",
    supportedLanguages:
      "Chinese (Traditional) / Chinese (Simplified) / English (UK) / English (US), French (France), German (Germany), Italian (Italy), Japanese (Japan), Korean (South Korea), Portuguese (Brazil), Spanish (Latin America), Spanish (Spain)",
  },
  securityCompliance: {
    securityCertifications: "ISO27001",
    dataLocation: "Japan",
    gdprCompliance: "Completed",
    accessControlType: "OAuth",
  },
  operationalStatus: {
    usageStatus: "Active",
    retirementDate: "-",
  },
  architecture: {
    versionBuildInformation: {
      version: "v5.3.1",
      buildNumber: "126",
      releaseDate: "1 Apr 2024",
      patchStatus: "Active",
      patchVersion: "1 Apr 2024",
      endOfSupportDate: "Active",
      versionPolicy: "LTS",
    },
    softwareComponent: {
      componentId: "CMP-00123",
      componentName: "User Authentication Module",
      requesterSubmitter: "https://github.com/example/auth-module",
      runtime: "Java SE 17",
    },
    softwareDependency: {
      dependencyId: "DEP-00123",
      dependsOnType: "OS",
      dependsOnId: "MW-00045",
      minVersion: "8.0.0",
      maxVersion: "10.5.x",
      requiredFlag: "Required",
    },
    softwareInstance: {
      instanceId: "INST-00123",
      instanceType: "SaasTenant",
      tenantDomain: "tenant123.example.com",
      environment: "Development",
      region: "ap-northeast-1 (Tokyo)",
      linkedSystems: "-",
    },
    installInformation: {
      installationDate: "1 Apr 2024",
      installationPath: "C:\\Program Files\\ExampleApp",
      installationMethod: "Online Installer",
      installationMediaInfo: "USB Drive (v2.0)",
      installedDevicesCount: "15",
    },
    executionRequirements: {
      requiredMemory: "8 GB RAM",
      requiredDiskSpace: "2 GB free space",
      dependentSoftware: "Java Runtime Environment 11",
      executionMode: "On-Demand",
    },
    securityCompliance: {
      requiresAdminPrivilege: "Yes",
      autoUpdateSetting: "Enabled",
      updateServerUrl: "https://updates.example.com",
      tamperDetectionEnabled: "True",
    },
    uninstallInformation: {
      uninstallationDate: "-",
      uninstallationMethod: "OS Reinstall",
      uninstalledBy: "-",
      uninstallationReason: "-",
    },
  },
  contractsCost: {
    contractInformation: {
      contractId: "CTR-2025-001",
      contractName: "Windows Server 2022 Standard",
      contractType: "Subscription Agreement",
      contractStatus: "Active",
      contractStartDate: "1 Apr 2024",
      contractEndDate: "1 Apr 2024",
      autoRenew: "Yes",
      renewalWindow: "30 days before contract end date",
      cancellationDeadline: "30 days prior to renewal date",
      noticePeriod: "30 days",
      contractAmount: "¥1,200,000 (tax excluded)",
      billingCycle: "Annual",
      paymentMethod: "Bank transfer",
      paymentTerms: "Net 30 days",
      paymentCurrency: "JPY",
      supplierId: "SUP-009",
      supplierContact: "John Smith, john.smith@example.com",
      contractFileUrl: "https://drive.example.com/contracts/CTR-2025-001",
      contractNotes: "Renewal price increase of 5% agreed",
    },
    planInformation: {
      planId: "PLAN-ENT-001",
      planName: "Enterprise",
      planCode: "ENT2025",
      planType: "Per-user subscription",
      pricingModel: "Per user per month",
      pricingDriver: "Number of active users",
      currency: "JPY",
      unitPrice: "¥1,200",
      baseFee: "¥120,000",
      variableFee: "Usage over 100 users billed separately",
      usageUnit: "Active user account",
      usageLimit: "500 accounts",
      includedFeatures:
        "Custom domain, 1TB cloud storage per user, priority support",
      planNotes: "Special campaign discount applied for first year",
    },
    budgetCost: {
      costMonth: "1 Apr 2024",
      budgetedCost: "¥1,200,000",
      actualCost: "¥1,260,000",
      varianceReason: "Additional users added mid-year",
      costCenter: "IT Department",
      invoiceNumber: "INV-2025-1001",
      taxRate: "10%",
    },
  },
  activityMonitoring: {
    accountAccessActivity: [
      {
        accountId: "admin.server",
        accountName: "Server Administrator",
        accountAvatar: "https://example.com/avatars/admin.jpg",
        activityType: "Login",
        activityDescription: "Administrative login to server",
        deviceId: "SRV-001",
        deviceName: "Production Server 1",
        deviceType: "server",
        deviceModel: "Dell PowerEdge R750",
        ipAddress: "192.168.1.10",
      },
      {
        accountId: "backup.service",
        accountName: "Backup Service Account",
        accountAvatar: "https://example.com/avatars/service.jpg",
        activityType: "System Access",
        activityDescription: "Automated backup process",
        deviceId: "SRV-002",
        deviceName: "Backup Server",
        deviceType: "server",
        deviceModel: "Dell PowerEdge R640",
        ipAddress: "192.168.1.11",
      },
    ],
    accountAccessAudit: {
      auditReviewStatus: "Confirmed",
      reviewedBy: "Alex Thompson",
      reviewedAt: "2024-01-15",
    },
  },
};

const AstraCoreMock: SoftwareMock = {
  name: "AstraCore",
  softwareId: "so-0sgese",
  vendorName: "Astra Technologies Inc.",
  vendorAvatarUrl: "https://example.com/logo/astracore.png",
  nextRenewalDate: "2025-09-01",
  status: "Active",
  assigned: 150,
  total: 200,
  hasPricing: true,
  currencyCode: "USD",
  detail: {
    softwareId: "so-0sgese",
    softwareName: "AstraCore",
    softwareAlias: "CoreERP",
    softwareType: "Enterprise Application",
    category: "ERP",
    description:
      "ERP system that unifies HR, accounting, procurement and supply chain operations across the enterprise",
  },
  link: {
    servicePageUrl: "https://astra-core.example",
    termsOfUseUrl: "https://astra-core.example/legal/terms",
    privacyPolicyUrl: "https://astra-core.example/legal/privacy",
    pricingPageUrl: "https://astra-core.example/pricing",
  },
  responsibility: {
    responsiblePersonId: "EMP-210",
    responsiblePersonName: "Morgan Lee",
    responsibleDepartment: "Business Systems Dept.",
  },
  vendor: {
    vendorId: "VND-010",
    vendorName: "Astra Technologies Inc.",
    vendorContact: "erp-support@astra-core.example",
    vendorUrl: "https://astra-core.example",
  },
  maintenanceSupport: {
    supportLevel: "Enterprise Premium Support",
    updateFrequency: "Quarterly",
  },
  provisioningDistribution: {
    deploymentMethod: "Hybrid (Cloud + On-prem)",
    platformUsage: "Desktop/Mobile",
    linkedDeviceId: "-",
    linkedSoftwareId: "-",
    mdmManaged: "Yes",
  },
  compatibilityEnvironment: {
    requiredOs: "RHEL 8/Windows Server 2022",
    compatibleBrowsers: "Chrome/Edge/Safari",
    supportedLanguages: "English",
  },
  securityCompliance: {
    securityCertifications: "ISO 27001/SOC 2 Type II",
    dataLocation: "US",
    gdprCompliance: "Completed",
    accessControlType: "SSO",
  },
  operationalStatus: {
    usageStatus: "Active",
    retirementDate: "-",
  },
  architecture: {
    versionBuildInformation: {
      version: "7.2",
      buildNumber: "7.2.451",
      releaseDate: "2025-07-01",
      patchStatus: "Pending",
      patchVersion: "HF-7.2.2",
      endOfSupportDate: "2028-07-01",
      versionPolicy: "LTS (3 years)",
    },
    softwareComponent: {
      componentId: "CMP-500",
      componentName: "Workflow Engine",
      requesterSubmitter: "https://git.astra-core.example/workflow-engine",
      runtime: "Java (Spring Boot)",
    },
    softwareDependency: {
      dependencyId: "-",
      dependsOnType: "-",
      dependsOnId: "-",
      minVersion: "19c",
      maxVersion: "23c",
      requiredFlag: "TRUE",
    },
    softwareInstance: {
      instanceId: "INST-AC-01",
      instanceType: "Multi-tenant SaaS Instance",
      tenantDomain: "astracore.dresscode.com",
      environment: "Production",
      region: "Japan",
      linkedSystems: "-",
    },
    installInformation: {
      installationDate: "2025-04-01",
      installationPath: "/opt/astracore",
      installationMethod: "Kubernetes Helm Chart",
      installationMediaInfo: "Docker Image Registry",
      installedDevicesCount: "10",
    },
    executionRequirements: {
      requiredMemory: "64GB",
      requiredDiskSpace: "500GB",
      dependentSoftware: "Oracle DB; Redis; Kafka",
      executionMode: "Web Application Cluster",
    },
    securityCompliance: {
      requiresAdminPrivilege: "Yes",
      autoUpdateSetting: "Manual (quarterly)",
      updateServerUrl: "https://updates.astra-core.example",
      tamperDetectionEnabled: "Yes",
    },
    uninstallInformation: {
      uninstallationDate: "-",
      uninstallationMethod: "Kubernetes Helm Uninstall",
      uninstalledBy: "DevOps Team",
      uninstallationReason: "System Migration",
    },
  },
  contractsCost: {
    contractInformation: {
      contractId: "CTR-2025-002",
      contractName: "AstraCore Enterprise License",
      contractType: "Enterprise License Agreement",
      contractStatus: "Active",
      contractStartDate: "2025-04-01",
      contractEndDate: "2026-04-01",
      autoRenew: "Yes",
      renewalWindow: "60 days before contract end date",
      cancellationDeadline: "60 days prior to renewal date",
      noticePeriod: "60 days",
      contractAmount: "$2,400,000 (tax excluded)",
      billingCycle: "Annual",
      paymentMethod: "Bank transfer",
      paymentTerms: "Net 30 days",
      paymentCurrency: "USD",
      supplierId: "VND-010",
      supplierContact: "erp-support@astra-core.example",
      contractFileUrl: "https://drive.example.com/contracts/CTR-2025-002",
      contractNotes: "Enterprise ERP system with premium support",
    },
    planInformation: {
      planId: "PLAN-AC-ENT-001",
      planName: "AstraCore Enterprise",
      planCode: "ACENT2025",
      planType: "Enterprise license",
      pricingModel: "Per user per month",
      pricingDriver: "Number of active users",
      currency: "USD",
      unitPrice: "$200",
      baseFee: "$50,000",
      variableFee: "Per user billing above base",
      usageUnit: "Active user account",
      usageLimit: "Unlimited",
      includedFeatures: "Full ERP suite, premium support, custom integrations",
      planNotes: "Enterprise plan with dedicated support",
    },
    budgetCost: {
      costMonth: "2025-04-01",
      budgetedCost: "$30,000",
      actualCost: "$30,000",
      varianceReason: "On budget",
      costCenter: "Business Systems Dept.",
      invoiceNumber: "INV-2025-2001",
      taxRate: "10%",
    },
  },
  activityMonitoring: {
    accountAccessActivity: [
      {
        accountId: "morgan.lee",
        accountName: "Morgan Lee",
        accountAvatar: "https://example.com/avatars/morgan.jpg",
        activityType: "Login",
        activityDescription: "Accessed ERP dashboard",
        deviceId: "ML001LAPTOP",
        deviceName: "Morgan's Laptop",
        deviceType: "laptop",
        deviceModel: "ThinkPad X1",
        ipAddress: "192.168.1.200",
      },
      {
        accountId: "alex.johnson",
        accountName: "Alex Johnson",
        accountAvatar: "https://example.com/avatars/alex.jpg",
        activityType: "Data Export",
        activityDescription: "Exported financial reports",
        deviceId: "AJ001DESKTOP",
        deviceName: "Alex's Workstation",
        deviceType: "desktop",
        deviceModel: "Dell OptiPlex",
        ipAddress: "192.168.1.201",
      },
      {
        accountId: "sam.rivera",
        accountName: "Sam Rivera",
        accountAvatar: "https://example.com/avatars/sam.jpg",
        activityType: "Configuration Change",
        activityDescription: "Updated workflow settings",
        deviceId: "SR001LAPTOP",
        deviceName: "Sam's Laptop",
        deviceType: "laptop",
        deviceModel: "MacBook Pro",
        ipAddress: "192.168.1.202",
      },
    ],
    accountAccessAudit: {
      auditReviewStatus: "Confirmed",
      reviewedBy: "Morgan Lee",
      reviewedAt: "2025-04-01",
    },
  },
};

export const getSoftwareMock = (softwareId: string): SoftwareMock => {
  if (softwareId === "so-098492") {
    return PowerPointMock;
  } else if (softwareId === "so-ws2022") {
    return WindowsServerMock;
  } else {
    return AstraCoreMock;
  }
};

// Overviewに関しては一旦共通とする
export const getSoftwareOverviewMonthlyCostMock = (
  _softwareId: string,
): SoftwareOverviewMonthlyCostMock => {
  return {
    cost: 2640,
    currencyCode: "USD",
    monthOverMonthRate: 0.12,
  };
};

export const getSoftwareOverviewUserMock = (
  _softwareId: string,
): SoftwareOverviewUserMock => {
  return {
    appUserCount: 0,
    peopleCount: 0,
    monthOverMonthChange: 0,
  };
};

export const getSoftwareOverviewMonthlyActiveUserRateMock = (
  _softwareId: string,
): SoftwareOverviewMonthlyActiveUserRateMock => {
  return {
    activeUserRate: 0,
    monthOverMonthRate: 0,
  };
};

export const getSoftwareOverviewRenewalDateMock = (
  _softwareId: string,
): SoftwareOverviewRenewalDateMock => {
  return {
    renewalDate: null,
  };
};

export const getSoftwareOverviewSavingCostMock = (
  _softwareId: string,
): SoftwareOverviewSavingCostMock => {
  return {
    savingCost: 0,
    currencyCode: "USD",
    monthOverMonthChange: 0,
  };
};

export const getSoftwareOverviewBillingCostMock = (
  _softwareId: string,
): SoftwareOverviewBillingCostMock => {
  return {
    monthlyCosts: [
      // { year: 2024, month: 6, cost: 2400, cumulativeCost: 2400 },
      // { year: 2024, month: 7, cost: 2500, cumulativeCost: 4900 },
      // { year: 2024, month: 8, cost: 2640, cumulativeCost: 7540 },
    ],
    forecastedCosts: [
      // {
      //   year: 2024,
      //   month: 9,
      //   forecastedCost: 2700,
      //   forecastedCumulativeCost: 10240,
      // },
      // {
      //   year: 2024,
      //   month: 10,
      //   forecastedCost: 2750,
      //   forecastedCumulativeCost: 12990,
      // },
    ],
    monthlyUsers: [
      // { year: 2024, month: 6, userCount: 110 },
      // { year: 2024, month: 7, userCount: 115 },
      // { year: 2024, month: 8, userCount: 120 },
    ],
  };
};

export const getSoftwareOverviewUserGrowthMock = (
  _softwareId: string,
): SoftwareOverviewUserGrowthMock => {
  return {
    monthlyUsers: [
      // { year: 2024, month: 4, userCount: 95, cumulativeUserCount: 95 },
      // { year: 2024, month: 5, userCount: 105, cumulativeUserCount: 200 },
      // { year: 2024, month: 6, userCount: 110, cumulativeUserCount: 310 },
      // { year: 2024, month: 7, userCount: 115, cumulativeUserCount: 425 },
      // { year: 2024, month: 8, userCount: 120, cumulativeUserCount: 545 },
    ],
  };
};

export const getSoftwareOverviewRecommendedActionsMock = (
  _softwareId: string,
): SoftwareOverviewRecommendedActionsMock => {
  return [
    // {
    //   actionType: "SET_LICENSE_SEAT",
    //   title: "Optimize license allocation",
    //   description:
    //     "You have 30 unused licenses. Consider reducing your plan to save costs.",
    //   actionLabel: "Adjust licenses",
    // },
    // {
    //   actionType: "RE_AUTHENTICATION",
    //   title: "Re-authenticate connection",
    //   description:
    //     "Your PowerPoint integration needs re-authentication to continue syncing data.",
    //   actionLabel: "Re-authenticate",
    // },
  ];
};

export const getRefreshResourcesStatusMock = (
  _softwareId: string,
): RefreshResourcesStatusMock => {
  return {
    status: "COMPLETED",
  };
};
